import { Level } from "@cryptors/api-client";
import { Box, TextField } from "@mui/material";
import { HttpError, IResourceComponentsProps } from "@refinedev/core";
import { Create } from "@refinedev/mui";
import { useForm } from "@refinedev/react-hook-form";
import { useEffect } from "react";
import { Controller } from "react-hook-form";

import UploadButton from "@/components/forms/UploadButton";
import { getTranslation } from "@/utils/translation";
import { upload } from "@/utils/upload";

interface LevelCreateEditProps extends IResourceComponentsProps {
  isEditing?: boolean;
}

interface LevelFormValues {
  level?: number;
  nameTranslation: { value: string };
  pointsRequired: number;
  image: { url: string };
}

export const LevelCreateEdit: React.FC<LevelCreateEditProps> = ({
  isEditing = false,
}) => {
  const {
    refineCore: { formLoading, onFinish, query },
    register,
    formState: { errors, isValid, isDirty },
    control,
    handleSubmit,
    setError,
    setValue,
  } = useForm<Level, HttpError, LevelFormValues>();

  const data = query?.data?.data as Level;

  useEffect(() => {
    if (!data) return;
    setValue(
      "nameTranslation.value",
      getTranslation(data.nameTranslation)?.value ?? "",
    );
  }, [data, setValue]);

  const onFinishHandler = (data: LevelFormValues) => {
    onFinish({
      ...data,
      level: !isEditing ? data.level : undefined,
    });
  };

  return (
    <Create
      isLoading={formLoading}
      saveButtonProps={{
        onClick: handleSubmit(onFinishHandler),
        disabled: !isDirty || !isValid,
      }}
    >
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <TextField
          {...register("level", {
            required: "Ce champ est requis",
            valueAsNumber: true,
          })}
          defaultValue={null}
          error={!!errors.level}
          helperText={errors.level?.message}
          margin="normal"
          fullWidth
          type="number"
          slotProps={{ inputLabel: { shrink: true } }}
          label="Niveau"
          name="level"
          disabled={isEditing}
        />
        <TextField
          {...register("nameTranslation.value", {
            required: "Ce champ est requis",
          })}
          defaultValue={null}
          error={!!errors.nameTranslation?.value}
          helperText={errors.nameTranslation?.value?.message}
          margin="normal"
          fullWidth
          slotProps={{ inputLabel: { shrink: true } }}
          label="Nom"
          name="nameTranslation.value"
        />
        <TextField
          {...register("pointsRequired", {
            required: "Ce champ est requis",
            valueAsNumber: true,
          })}
          defaultValue={null}
          error={!!errors.pointsRequired}
          helperText={errors.pointsRequired?.message}
          margin="normal"
          fullWidth
          type="number"
          slotProps={{ inputLabel: { shrink: true } }}
          label="Points requis"
          name="pointsRequired"
        />
        <Controller
          control={control}
          name="image.url"
          defaultValue=""
          rules={{ required: "Ce champ est requis" }}
          render={({ field, fieldState }) => (
            <UploadButton
              id="image"
              inputSrc={field.value}
              uploadButtonLabel="Upload badge"
              onError={() =>
                setError("image.url", {
                  message: "L'upload a échoué. Veuillez réessayer",
                })
              }
              onChange={async (event) => {
                const url = await upload(event, "badges");
                field.onChange(url);
              }}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
      </Box>
    </Create>
  );
};
